'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { playSuccess, playError, playWarning } from '../lib/sound';
import { useTranslation } from '../lib/i18n';
import { useExitAnimation } from '../lib/useExitAnimation';

type ToastType = 'success' | 'error' | 'info' | 'warning';

type Toast = {
  id: number;
  message: string;
  type: ToastType;
};

type ToastContextType = {
  showToast: (message: string, type?: ToastType) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

let toastId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [leavingIds, setLeavingIds] = useState<Set<number>>(new Set());
  const [modalToast, setModalToast] = useState<Toast | null>(null);
  const modalExiting = useExitAnimation(modalToast != null);
  const [lastModalToast, setLastModalToast] = useState(modalToast);
  if (modalToast && modalToast !== lastModalToast) setLastModalToast(modalToast);
  const modalToastShown = modalToast ?? lastModalToast;
  const [copied, setCopied] = useState(false);

  const removeToast = useCallback((id: number) => {
    // Nejprve exit animace (~250 ms), teprve pak skutečné odebrání z pole
    setLeavingIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      setLeavingIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }, 250);
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = toastId++;
    setToasts((prev) => [...prev, { id, message, type }]);
    
    if (type === 'success') playSuccess();
    else if (type === 'error') playError();
    else if (type === 'warning') playWarning();
    
    // Auto remove after 4 seconds
    setTimeout(() => {
      removeToast(id);
    }, 4000);
  }, [removeToast]);

  const handleToastClick = (toast: Toast) => {
    setModalToast(toast);
    removeToast(toast.id);
  };

  const handleCopy = async () => {
    if (!modalToastShown) return;
    try {
      await navigator.clipboard.writeText(modalToastShown.message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const closeModal = () => {
    setModalToast(null);
    setCopied(false);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
        {toasts.map((toast) => {
          let styles = '';
          let icon = '';
          if (toast.type === 'success') {
            styles = 'bg-moss text-cream border-moss-dark';
            icon = '✓';
          } else if (toast.type === 'error') {
            styles = 'bg-rust text-cream border-rust';
            icon = '✕';
          } else if (toast.type === 'warning') {
            styles = 'bg-ochre text-bark border-ochre-dark';
            icon = '⚠';
          } else {
            styles = 'bg-wood text-cream border-bark';
            icon = 'ℹ';
          }

          return (
            <div
              key={toast.id}
              onClick={() => handleToastClick(toast)}
              className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-rustic border shadow-lg transition-all duration-300 transform translate-y-0 scale-100 ${leavingIds.has(toast.id) ? 'animate-toastOut' : 'animate-slide-in'} cursor-pointer hover:scale-[1.02] ${styles}`}
            >
              <span className="flex items-center justify-center font-bold text-sm bg-bark/15 rounded-full w-5 h-5 shrink-0 mt-0.5">
                {icon}
              </span>
              <p className="text-sm font-medium flex-1">{toast.message}</p>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeToast(toast.id); }}
                className="text-cream/70 hover:text-cream transition text-xs font-bold leading-none shrink-0"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>

      {/* Toast detail modal */}
      {(modalToast || modalExiting) && modalToastShown && (
        <div
          className={`fixed inset-0 z-[10000] flex items-center justify-center bg-bark/40 backdrop-blur-sm ${modalToast ? 'animate-backdropIn' : 'animate-backdropOut'}`}
          onClick={closeModal}
        >
          <div
            role="dialog" aria-modal="true"
            className={`bg-cream rounded-rustic shadow-2xl border border-wheat max-w-lg w-full mx-4 p-5 ${modalToast ? 'animate-modalIn' : 'animate-modalOut'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <span className="flex items-center justify-center font-bold text-sm bg-bark/15 rounded-full w-6 h-6 shrink-0 mt-0.5">
                {modalToastShown.type === 'success' ? '✓' : modalToastShown.type === 'error' ? '✕' : modalToastShown.type === 'warning' ? '⚠' : 'ℹ'}
              </span>
              <p className="text-sm font-medium text-bark whitespace-pre-wrap break-words flex-1">{modalToastShown.message}</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="btn-secondary px-4 py-1.5 text-sm"
              >
                {copied ? t('toastCopied') : t('toastCopy')}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="btn-primary px-4 py-1.5 text-sm"
              >
                {t('toastClose')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
